/**
 * Profil pracownika (spec 01 §3) — READ-ONLY, 9 zakładek, oś 10 etapów
 * i boczny panel postępu onboardingu.
 *
 * Widoczność pól rozstrzyga serwer: gdy rola nie obejmuje danej sekcji, API
 * w ogóle nie przysyła danych, a ekran mówi o tym wprost zamiast pokazywać
 * puste pola udające brak wartości.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Accordion,
  Avatar,
  Button,
  Card,
  Check,
  Checkbox,
  ChevronLeft,
  EmptyState,
  Info,
  Input,
  Select,
  ShieldCheckIcon,
  Spinner,
  StatusPill,
  Tabs,
  cx,
  useToast,
} from '@k1p4/ui'
import {
  fileChecklistPercent,
  isManualFileChecklistKey,
  onboardingStepState,
  ONBOARDING_STAGES,
  ONE_TIME_BHP_KINDS,
  isEmploymentContract,
  isWithholdingContract,
  type BhpTrainingKind,
  type HealthState,
} from '@k1p4/shared'
import { formatDate, formatDateTime, formatPLN } from '@k1p4/i18n'
import {
  api,
  ApiError,
  openFile,
  type ChangeRequestField,
  type EmployeeProfileResponse,
} from '../../api/client'
import { useHasPermission, useHasRole } from '../../auth/AuthProvider'
import { ChangeRequestModal } from './ChangeRequestModal'
import { OffboardingModal } from './OffboardingModal'
import { TermsChangeModal } from './TermsChangeModal'
import { ChangeRequestsTab } from './ChangeRequestsTab'
import { OnboardingPanel } from './OnboardingPanel'
import { FamilyMembers } from './FamilyMembers'
import { Penalties } from './Penalties'
import { PersonnelFile } from './PersonnelFile'
import { RecordCard } from './RecordCard'
import { DzieloContracts } from './DzieloContracts'
import { IftRequests } from './IftRequests'
import { TimeReview } from './TimeReview'
import { WorkSchedule } from './WorkSchedule'
import { Garnishments } from './Garnishments'
import styles from './EmployeeProfile.module.css'
import { useFormat } from '../../format'

const TAB_KEYS = [
  'summary',
  'personal',
  'employment',
  'salary',
  'bank',
  'documents',
  'health',
  'requests',
  'audit',
] as const

type TabKey = (typeof TAB_KEYS)[number]

/** Pole read-only; brak wartości pokazujemy pauzą, nie pustym miejscem. */
function Field({
  label,
  value,
  mono = false,
  warn = false,
  action,
}: {
  label: string
  value: string | null | undefined
  mono?: boolean
  warn?: boolean
  action?: React.ReactNode
}) {
  return (
    <div className={styles.field}>
      <p className={styles.fieldLabel}>{label}</p>
      <p
        className={cx(
          styles.fieldValue,
          mono && styles.fieldMono,
          warn && styles.fieldWarn,
          (value === null || value === undefined || value === '') && styles.fieldEmpty,
        )}
      >
        {value !== null && value !== undefined && value !== '' ? value : '—'}
        {action}
      </p>
    </div>
  )
}

/** Kwoty w groszach czytamy jak kwoty, resztę wprost — jak w portalu. */
function annexValue(field: string, value: string | null): string {
  if (value === null) return '—'
  if (field.endsWith('Gr')) return formatPLN(Number(value))
  return value
}

function ApprovalFooter({
  approvedBy,
  approvedAt,
  labelKey = 'profile.approvedBy',
  onRequestChange,
}: {
  approvedBy: string | null
  approvedAt: string | null
  /** Zdanie stopki — warunki umowy są PRZYJMOWANE przez pracownika, nie zatwierdzane (grupa 106). */
  labelKey?: string
  /** Brak funkcji = rola nie może wnioskować; przycisk wtedy nie istnieje. */
  onRequestChange?: () => void
}) {
  const fmt = useFormat()
  const { t } = useTranslation()
  return (
    <div className={styles.footerMeta}>
      <span>
        {/*
          Chwila z bazy to znacznik czasu — dzień liczymy w strefie przeglądarki,
          a nie z UTC (weryfikacja na żywo Fali 6: 01:57 w Polsce to poprzedni dzień w UTC).
        */}
        {approvedBy && approvedAt
          ? t(labelKey, {
              who: approvedBy,
              when: fmt.date(approvedAt, 'long'),
            })
          : approvedAt && labelKey === 'profile.termsAcceptedFooter'
            ? t('profile.termsAcceptedNoAuthor', {
                when: fmt.date(approvedAt, 'long'),
              })
            : t('profile.notApproved')}
      </span>
      {onRequestChange ? (
        <Button size="sm" variant="secondary" onClick={onRequestChange}>
          {t('profile.requestChange')}
        </Button>
      ) : null}
    </div>
  )
}

/** Ton pigułki stanu badań — jak na liście „Badania i BHP". */
const HEALTH_TONE: Record<HealthState, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ok: 'success',
  warn: 'warning',
  err: 'danger',
  todo: 'warning',
  na: 'neutral',
  missing: 'warning',
}

function NoAccess() {
  const { t } = useTranslation()
  return <EmptyState icon={<ShieldCheckIcon size={26} />} title={t('profile.noAccess')} />
}

export function EmployeeProfile() {
  const fmt = useFormat()
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const toast = useToast()
  const canRunProcess = useHasRole('hr_admin')
  /* Wniosek o zmianę składa dział kadr; pracownik robi to z portalu. */
  const canRequestChange = useHasRole('hr_admin')
  /*
   * Pełny PESEL/IBAN odsłania osobne uprawnienie — payroll widzi zamaskowany
   * rachunek, ale przycisk „Pokaż" bez tego uprawnienia kończyłby się zawsze
   * odmową serwera, więc go nie renderujemy [D28].
   */
  const canRevealSensitive = useHasPermission('Kadry : Dane wrażliwe')
  /* Aneks przygotowują kadry z prawem edycji akt (Etap 14). */
  const canEditPersonnel = useHasPermission('Kadry : Edycja')
  const canEditDocuments = useHasPermission('Dokumenty : Edycja')
  /* Wykaz akt tylko z „Dokumenty : Podgląd" — bez niego trasa i tak odmówi (grupa 74). */
  const canViewDocuments = useHasPermission('Dokumenty : Podgląd') || canEditDocuments
  /* Składnik do listy płac dopisuje rola płacowa, nie kadry (Etap 23). */
  const canManagePayroll = useHasPermission('Płace : Zatwierdzanie')
  /*
   * Ochrona danych, rozliczenie końcowe i wnioski o zmianę danych to trasy kadr
   * i płac. Przełożony (sam zakres zespołu) dostałby na nich 403 i wpis odmowy
   * w dzienniku przy każdym wejściu na profil podwładnego — zapytań więc nie
   * wysyłamy, a zakładka mówi o braku dostępu (Fala 4, po przeglądzie).
   */
  const canViewPersonnel = useHasPermission('Kadry : Podgląd')
  const canViewPayroll = useHasPermission('Płace : Podgląd')
  /* Trasy czasu pracy w aktach pytają o „Czas pracy : Podgląd" albo „Akceptacja". */
  const canViewTimeRecords = useHasPermission('Czas pracy : Podgląd')
  const canApproveTime = useHasPermission('Czas pracy : Akceptacja')
  const canViewTime = canViewTimeRecords || canApproveTime
  const [annexOpen, setAnnexOpen] = useState(false)
  const [offboardingOpen, setOffboardingOpen] = useState(false)
  /* Powód anonimizacji wpisuje człowiek — bez niego serwer odmawia (Etap 20). */
  const [anonymizeReason, setAnonymizeReason] = useState('')
  /* Rozliczenie końcowe (Etap 23): człowiek wybiera pozycje i okres. */
  const [settlementPick, setSettlementPick] = useState({ equivalent: true, severance: false })
  const [settlementPeriod, setSettlementPeriod] = useState('')
  /* Ręczny składnik tego rodzaju już jest — dopisujemy dopiero po potwierdzeniu (#67). */
  const [duplicateOf, setDuplicateOf] = useState<Array<{ kind: string; month: string }> | null>(
    null,
  )
  const [changeField, setChangeField] = useState<ChangeRequestField | null>(null)
  const [tab, setTab] = useState<TabKey>('summary')
  const [revealed, setRevealed] = useState<Partial<Record<'pesel' | 'iban', string>>>({})
  const queryClient = useQueryClient()

  const withdrawAnnex = useMutation({
    mutationFn: () => api.withdrawTermsChange(id),
    onSuccess: () => {
      toast.show(t('profile.annex.withdrawn'), { tone: 'info' })
      void queryClient.invalidateQueries({ queryKey: ['employee', id] })
    },
  })

  const markItem = useMutation({
    mutationFn: (input: { itemKey: string; done: boolean }) =>
      api.setFileChecklistItem(id, input.itemKey, input.done),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee', id] })
    },
    onError: () => toast.show(t('profile.checklistError'), { tone: 'danger' }),
  })

  const profile = useQuery({
    queryKey: ['employee', id],
    queryFn: () => api.employee(id),
    retry: false,
  })
  /*
   * Rozliczenie końcowe: ekwiwalent za urlop i odprawa. Serwer PROPONUJE kwoty
   * z rozpisanym rachunkiem; o tym, czy się należą, decyduje człowiek — dlatego
   * pytamy tylko wtedy, gdy zatrudnienie już ustało (Etap 23, ADR-0024).
   */
  const settlement = useQuery({
    queryKey: ['employee', id, 'settlement'],
    queryFn: () => api.settlement(id),
    enabled:
      (canViewPersonnel || canViewPayroll) &&
      profile.data?.termination !== null &&
      profile.data?.termination !== undefined,
    retry: false,
  })
  const equivalentApplied =
    settlement.data?.applied.some((item) => item.kind === 'ekwiwalent' && item.fromSettlement) ??
    false
  const severanceApplied =
    settlement.data?.applied.some((item) => item.kind === 'odprawa' && item.fromSettlement) ?? false
  /*
   * Okres idzie ARGUMENTEM, nie z domknięcia: pierwsze kliknięcie ustawiało
   * stan i od razu wysyłało pusty `periodId`, dostając 400 (#67).
   */
  const applySettlement = useMutation({
    mutationFn: (vars: {
      periodId: string
      includeEquivalent: boolean
      includeSeverance: boolean
      confirmKinds: string[]
    }) => api.applySettlement(id, vars),
    onSuccess: (result) => {
      setDuplicateOf(null)
      toast.show(t('profile.settlement.applied', { count: result.added.length }), {
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['employee', id] })
    },
    onError: (caught, vars) => {
      if (
        !(caught instanceof ApiError) ||
        (caught.code !== 'ALREADY_APPLIED' && caught.code !== 'KIND_EXISTS')
      ) {
        toast.show(t('profile.settlement.error'), { tone: 'danger' })
        return
      }
      /*
       * Rodzaj i miesiąc kolizji podaje serwer — lokalna propozycja bywa
       * nieaktualna, bo blokada #67 istnieje właśnie na dwie karty. Pamięć
       * podręczna jest tylko zapasem, zawsze z filtrem po żądanym rodzaju.
       */
      void queryClient.invalidateQueries({ queryKey: ['employee', id, 'settlement'] })
      if (caught.code === 'ALREADY_APPLIED') {
        const requested = vars.includeEquivalent ? 'ekwiwalent' : 'odprawa'
        const cached = (settlement.data?.applied ?? []).find(
          (item) => item.kind === requested && item.fromSettlement,
        )
        const month =
          typeof caught.details.month === 'string' ? caught.details.month : (cached?.month ?? null)
        toast.show(
          month === null
            ? t('profile.settlement.alreadyAppliedErrorNoMonth')
            : t('profile.settlement.alreadyAppliedError', { month }),
          { tone: 'danger' },
        )
        return
      }
      /* Każdy rodzaj z ręcznym składnikiem ma własne zdanie i własne potwierdzenie. */
      const conflicts = Array.isArray(caught.details.conflicts)
        ? (caught.details.conflicts as Array<{ kind?: unknown; month?: unknown }>)
            .filter(
              (item): item is { kind: string; month: string } =>
                typeof item.kind === 'string' && typeof item.month === 'string',
            )
            .map((item) => ({ kind: item.kind, month: item.month }))
        : []
      setDuplicateOf(conflicts.length > 0 ? conflicts : null)
      if (conflicts.length === 0) toast.show(t('profile.settlement.error'), { tone: 'danger' })
    },
  })

  /* Okres przechowywania akt liczy serwer z przepisu (Etap 20, ADR-0021). */
  const dataProtection = useQuery({
    queryKey: ['employee', id, 'data-protection'],
    queryFn: () => api.dataProtection(id),
    enabled: canViewPersonnel,
    retry: false,
  })
  const anonymize = useMutation({
    mutationFn: () => api.anonymizeEmployee(id, anonymizeReason),
    onSuccess: () => {
      toast.show(t('profile.dataProtection.anonymized'), { tone: 'success' })
      setAnonymizeReason('')
      void queryClient.invalidateQueries({ queryKey: ['employee', id] })
    },
    onError: () => toast.show(t('profile.dataProtection.anonymizeError'), { tone: 'danger' }),
  })
  const audit = useQuery({
    queryKey: ['employee', id, 'audit'],
    queryFn: () => api.employeeAudit(id),
    enabled: tab === 'audit',
    retry: false,
  })

  if (profile.isPending) return <Spinner label={t('common.loading')} />
  if (profile.isError || !profile.data) {
    return (
      <EmptyState
        icon={<Info size={28} />}
        title={t('profile.notFound')}
        description={t('profile.notFoundBody')}
        cta={
          <Link to="/app/employees">
            <Button>{t('profile.back')}</Button>
          </Link>
        }
      />
    )
  }

  const data: EmployeeProfileResponse = profile.data
  const { employee, onboarding, personalData, taxData, bankAccount, employmentTerms } = data
  /*
   * Kary i dokumentacja czasu pracy dotyczą pracownika w stosunku pracy
   * (art. 108, art. 129 § 3, art. 149 k.p.). Akta bez rodzaju umowy (np. sprzed
   * warunków) traktujemy jak umowę o pracę — ostrożniej pokazać kartę niż ją
   * schować (fala 8, grupa 87; D98).
   */
  const laborCode = employee.employmentType === null || isEmploymentContract(employee.employmentType)
  /*
   * Etap 8 osiąga potwierdzenie pracownika, a zatwierdzenie kadr jest osobnym
   * krokiem przed BHP (przegląd Fali 6): do zatwierdzenia oś stoi na kroku 8.
   */
  const reachedStage = onboarding.awaitingMedicalApproval ? onboarding.stageNumber - 1 : onboarding.stageNumber
  const nextStepKey = onboarding.awaitingMedicalApproval
    ? 'onboardingNextStep.medicalAwaitingApproval'
    : `onboardingNextStep.${onboarding.stage}`
  const fullName = `${employee.firstName} ${employee.lastName}`
  const progressPercent = Math.round((onboarding.stageNumber / onboarding.totalStages) * 100)
  const doneItems = data.checklist.filter((item) => item.done).length

  async function reveal(field: 'pesel' | 'iban'): Promise<void> {
    try {
      const result = await api.revealEmployeeField(id, field)
      if (result.value) setRevealed((current) => ({ ...current, [field]: result.value! }))
    } catch {
      /* np. 503 przy braku kluczy szyfrujących — klik nie może przejść bez echa */
      toast.show(t('profile.revealError'), { tone: 'danger' })
    }
  }

  const revealAction = (field: 'pesel' | 'iban') =>
    revealed[field] || !canRevealSensitive ? null : (
      <Button
        size="sm"
        variant="ghost"
        className={styles.revealButton}
        onClick={() => void reveal(field)}
      >
        {t('profile.reveal')}
      </Button>
    )

  return (
    <>
      <Link to="/app/employees" className={styles.backLink}>
        <ChevronLeft size={16} />
        {t('profile.back')}
      </Link>

      <Card pad="lg" className={styles.headerCard}>
        <Avatar name={fullName} size={64} />
        <div className={styles.identity}>
          <div className={styles.nameRow}>
            <h2 className={styles.name}>{fullName}</h2>
            <StatusPill tone={employee.status === 'aktywny' ? 'success' : 'pink'} dot>
              {t(`employeeStatus.${employee.status}`)}
            </StatusPill>
          </div>
          <p className={styles.roleLine}>
            {[employee.position, employee.department, employee.location]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
          <p className={styles.idLine}>
            {t('profile.idLine', {
              id: employee.displayId,
              manager: employee.manager ?? t('profile.noManager'),
              type: employee.employmentType
                ? t(`contractType.${employee.employmentType}`)
                : t('profile.noManager'),
            })}
          </p>
        </div>
        <div className={styles.headerChips}>
          <StatusPill tone={onboarding.fileComplete ? 'success' : 'warning'}>
            {t(onboarding.fileComplete ? 'profile.fileComplete' : 'profile.fileIncomplete')}
          </StatusPill>
          {/* Po ustaniu zatrudnienia nie ma dopuszczenia do pracy — jest data ustania (grupa 105). */}
          {onboarding.employmentEnded ? (
            <StatusPill tone="neutral">
              {t('profile.employmentEndedPill', {
                date: onboarding.employmentEndedOn ? formatDate(onboarding.employmentEndedOn, 'dot') : '—',
              })}
            </StatusPill>
          ) : (
            <StatusPill tone={onboarding.workAdmitted ? 'success' : 'neutral'}>
              {t(onboarding.workAdmitted ? 'profile.admitted' : 'profile.notAdmitted')}
            </StatusPill>
          )}
        </div>
      </Card>

      <p className={styles.protectedBanner}>
        <ShieldCheckIcon size={16} />
        {t('profile.protectedBanner')}
      </p>

      {onboarding.stageNumber < onboarding.totalStages ? (
        <p className={styles.nextStepBanner}>
          <Info size={16} />
          <span>
            <span className={styles.nextStepLabel}>{t('profile.nextStepLabel')}</span>
            {t(nextStepKey)}
          </span>
        </p>
      ) : null}

      <div className={styles.layout}>
        <Card pad="lg">
          <Tabs
            items={TAB_KEYS.map((key) => ({ key, label: t(`profile.tabs.${key}`) }))}
            value={tab}
            onChange={(next) => setTab(next as TabKey)}
          />

          <div className={styles.tabPanel}>
            {tab === 'summary' ? (
              <>
                <div className={styles.fieldGrid}>
                  <Field
                    label={t('profile.fields.status')}
                    value={t(`employeeStatus.${employee.status}`)}
                  />
                  <Field
                    label={t('profile.fields.type')}
                    value={
                      employee.employmentType ? t(`contractType.${employee.employmentType}`) : null
                    }
                  />
                  <Field label={t('profile.fields.manager')} value={employee.manager} />
                  <Field label={t('profile.fields.dept')} value={employee.department} />
                </div>

                <p className={styles.processTitle}>{t('profile.processTitle')}</p>
                <ol className={styles.timeline}>
                  {ONBOARDING_STAGES.map((stage, index) => {
                    const number = index + 1
                    /* Etap osiągnięty jest zrobiony, trwa następny (Fala 6, grupa 88). */
                    const step = onboardingStepState(number, reachedStage)
                    const state =
                      step === 'done' ? 'stageDone' : step === 'active' ? 'stageActive' : 'stagePending'
                    return (
                      <li key={stage} className={cx(styles.stage, styles[state])}>
                        <span className={styles.stageMarker} aria-hidden="true">
                          {state === 'stageDone' ? <Check size={15} /> : number}
                        </span>
                        <span>
                          <p className={styles.stageLabel}>{t(`onboardingStages.${stage}`)}</p>
                          <p className={styles.stageOwner}>
                            {t('profile.stageOwner', { owner: t(`onboardingOwners.${stage}`) })}
                          </p>
                        </span>
                        <StatusPill
                          className={styles.stagePill}
                          tone={
                            state === 'stageDone'
                              ? 'success'
                              : state === 'stageActive'
                                ? 'pink'
                                : 'neutral'
                          }
                        >
                          {t(
                            state === 'stageDone'
                              ? 'profile.stageDone'
                              : state === 'stageActive'
                                ? 'profile.stageActive'
                                : 'profile.stagePending',
                          )}
                        </StatusPill>
                      </li>
                    )
                  })}
                </ol>
              </>
            ) : null}

            {tab === 'personal' ? (
              personalData ? (
                <>
                  <span className={styles.readOnlyLabel}>
                    <ShieldCheckIcon size={13} />
                    {t('profile.readOnly')}
                  </span>
                  <div className={styles.fieldGrid}>
                    <Field
                      label={t('profile.fields.pesel')}
                      value={revealed.pesel ?? personalData.pesel}
                      mono
                      {...(personalData.pesel ? { action: revealAction('pesel') } : {})}
                    />
                    <Field
                      label={t('profile.fields.birthDate')}
                      value={personalData.birthDate ? fmt.date(personalData.birthDate, 'long') : null}
                    />
                    <Field
                      label={t('profile.fields.address')}
                      value={[personalData.addressStreet, personalData.addressCityZip]
                        .filter(Boolean)
                        .join(', ')}
                    />
                    <Field
                      label={t('profile.fields.citizenship')}
                      value={personalData.citizenship}
                    />
                    <Field label={t('profile.fields.email')} value={personalData.privateEmail} />
                    <Field label={t('profile.fields.phone')} value={personalData.phone} mono />
                    {taxData ? (
                      <>
                        <Field label={t('profile.fields.taxOffice')} value={taxData.taxOffice} />
                        <Field label={t('profile.fields.nfzBranch')} value={taxData.nfzBranch} />
                      </>
                    ) : null}
                  </div>
                  <ApprovalFooter
                    approvedBy={personalData.approvedBy}
                    approvedAt={
                      personalData.approvedAt
                    }
                    {...(canRequestChange
                      ? { onRequestChange: () => setChangeField('address') }
                      : {})}
                  />
                  {/*
                    Członkowie rodziny w ubezpieczeniu zdrowotnym (Etap 30):
                    formularz ZUS ZCNA składa płatnik w 7 dni od dnia, w którym
                    pracownik go o tym poinformował.
                  */}
                  <FamilyMembers employeeId={employee.id} canEdit={canEditPersonnel} />
                </>
              ) : (
                <NoAccess />
              )
            ) : null}

            {tab === 'employment' ? (
              <>
                <span className={styles.readOnlyLabel}>
                  <ShieldCheckIcon size={13} />
                  {t('profile.readOnly')}
                </span>
                <div className={styles.fieldGrid}>
                  <Field label={t('profile.fields.position')} value={employee.position} />
                  <Field label={t('profile.fields.dept')} value={employee.department} />
                  <Field
                    label={t('profile.fields.type')}
                    value={
                      employee.employmentType ? t(`contractType.${employee.employmentType}`) : null
                    }
                  />
                  <Field
                    label={t('profile.fields.hiredAt')}
                    value={employee.hiredAt ? fmt.date(employee.hiredAt, 'long') : null}
                  />
                  {/* Etat to pojęcie umowy o pracę — przy zleceniu, dziele i B2B go nie ma (przegląd Fali 6). */}
                  {employee.employmentType === null || isEmploymentContract(employee.employmentType) ? (
                    <Field
                      label={t('profile.fields.fte')}
                      value={
                        Number(employee.fte) === 1
                          ? t('profile.fields.fullTime')
                          : t('profile.fields.partTime', { value: fmt.decimal(employee.fte) })
                      }
                    />
                  ) : null}
                  <Field
                    label={t('profile.fields.contractEnd')}
                    value={
                      employee.contractEndAt ? fmt.date(employee.contractEndAt, 'long') : null
                    }
                    warn={employee.contractEndAt !== null}
                  />
                </div>
                {/*
                  Karty procesów stoją w „Zatrudnieniu" (fala 8, grupa 87; D98):
                  kary (ADR-0039, D72), dokumentacja czasu pracy (ADR-0042),
                  rejestr RUD i wnioski IFT-1 (D86). Każda karta jest bramkowana
                  uprawnieniem swojej trasy odczytu — przełożony bez „Kadry :
                  Podgląd" nie dostaje odmowy z serwera — i rodzajem umowy.
                */}
                {canViewPersonnel ? (
                  /*
                    Kary porządkowe (Etap 38): postępowanie z art. 108–113 k.p.
                    dotyczy pracownika — przy umowie cywilnej tylko odczyt
                    wpisów sprzed fali 8.
                  */
                  <Penalties
                    employeeId={employee.id}
                    canEdit={canEditPersonnel}
                    applicable={laborCode}
                  />
                ) : null}
                {canViewTime && laborCode ? (
                  /*
                    Dokumentacja czasu pracy pracownika (art. 129 § 3, art. 149
                    k.p.): karta ewidencji (Etap 39), rozkład (Etap 41)
                    i wykonanie kontra rozkład (Etapy 42–43) w jednym akordeonie,
                    żeby zakładka nie urosła o kilka ekranów.
                  */
                  <section className={styles.timeSection}>
                    <h3 className={styles.timeSectionTitle}>{t('profile.timeSection.title')}</h3>
                    <Accordion
                      defaultOpenIndex={-1}
                      items={[
                        {
                          key: 'schedule',
                          question: t('profile.timeSection.schedule'),
                          answer: (
                            <WorkSchedule
                              employeeId={employee.id}
                              canEdit={canEditPersonnel}
                              employmentEndedOn={
                                onboarding.employmentEnded ? (onboarding.employmentEndedOn ?? '') : null
                              }
                            />
                          ),
                        },
                        {
                          key: 'review',
                          question: t('profile.timeSection.review'),
                          answer: (
                            <TimeReview
                              employeeId={employee.id}
                              canEdit={canEditPersonnel}
                              canSetPay={data.scope.compensation}
                            />
                          ),
                        },
                        {
                          key: 'record',
                          question: t('profile.timeSection.record'),
                          answer: <RecordCard employeeId={employee.id} canIssue={canEditPersonnel} />,
                        },
                      ]}
                    />
                  </section>
                ) : null}
                {canViewPersonnel ? (
                  /*
                    Umowy o dzieło i zgłoszenia RUD (Etap 47): art. 36 ust. 17
                    u.s.u.s. — dotyczy umowy o dzieło, nie własnego pracownika.
                  */
                  <DzieloContracts
                    employeeId={employee.id}
                    canEdit={canEditPersonnel}
                    applicable={employee.employmentType === 'dzielo'}
                  />
                ) : null}
                {canManagePayroll ? (
                  /*
                    Wnioski o IFT-1 (Etap 52, art. 42 ust. 4 ustawy o PIT):
                    czynność płacowa z uprawnieniem płacowym — dawniej karta
                    pokazywała się każdemu i hr_admin dostawał 403 z serwera.
                  */
                  <IftRequests
                    employeeId={employee.id}
                    canEdit={canManagePayroll}
                    applicable={
                      employee.employmentType !== null && isWithholdingContract(employee.employmentType)
                    }
                  />
                ) : null}
              </>
            ) : null}

            {tab === 'employment' && data.termination ? (
              /*
                Ustanie zatrudnienia (Etap 15): tryb i podstawa prawna są tymi
                samymi, które wchodzą do bloku V formularza ZUS ZWUA — dlatego
                pokazujemy je wprost, razem z dniem wyrejestrowania.
              */
              <Card pad="md" className={styles.annexCard}>
                <p className={styles.annexTitle}>
                  {t('profile.offboarding.recordedOn', {
                    date: fmt.date(data.termination.endDate, 'long'),
                  })}
                </p>
                <ul className={styles.annexList}>
                  <li>
                    {t('profile.offboarding.mode')}:{' '}
                    {t(`profile.offboarding.modes.${data.termination.mode}`)} ({data.termination.zusMode})
                  </li>
                  {data.termination.legalBasis !== null ? (
                    <li>
                      {t('profile.offboarding.legalBasis')}: {data.termination.legalBasis}
                    </li>
                  ) : null}
                  {data.termination.initiator !== null ? (
                    <li>
                      {t('profile.offboarding.initiator')}:{' '}
                      {t(`profile.offboarding.initiators.${data.termination.initiator}`)}
                    </li>
                  ) : null}
                  {data.termination.reason !== null ? <li>{data.termination.reason}</li> : null}
                </ul>
              </Card>
            ) : null}

            {tab === 'employment' && settlement.data !== undefined ? (
              /*
                Rozliczenie końcowe (Etap 23). Pokazujemy CAŁY rachunek —
                współczynnik, stawkę za dzień i za godzinę — bo to jedyny sposób,
                żeby człowiek mógł go sprawdzić przed dopisaniem do listy płac.
              */
              <Card pad="md" className={styles.annexCard}>
                <p className={styles.annexTitle}>{t('profile.settlement.title')}</p>
                <ul className={styles.annexList}>
                  {settlement.data.leaveEquivalent === null ? (
                    <li>{t('profile.settlement.noEquivalent')}</li>
                  ) : (
                    <>
                      <li>
                        {t('profile.settlement.equivalent', {
                          /* Liczba i odmiana „dni” z formatera języka (fala 9, grupa 93). */
                          count: settlement.data.leaveEquivalent.unusedDays,
                          hours: settlement.data.leaveEquivalent.unusedHours.toLocaleString('pl-PL'),
                          coefficient: (
                            settlement.data.leaveEquivalent.coefficientCenti / 100
                          ).toLocaleString('pl-PL', { minimumFractionDigits: 2 }),
                          perHour: formatPLN(settlement.data.leaveEquivalent.perHourGr),
                          amount: formatPLN(settlement.data.leaveEquivalent.amountGr),
                        })}
                      </li>
                      {/*
                        Skąd liczba dni (#6): wymiar roku ustania proporcjonalnie
                        do miesięcy pracy, minus wnioski do dnia ustania — i to,
                        czego system nie wie (staż z art. 154 § 1, etat sprzed
                        historii warunków).
                      */}
                      {settlement.data.leaveEquivalent.proportionalMonths !== null ? (
                        <li>
                          {t('profile.settlement.proportionalNote', {
                            count: settlement.data.leaveEquivalent.entitlementDays,
                            months: settlement.data.leaveEquivalent.proportionalMonths,
                            fullDays: settlement.data.leaveEquivalent.fullYearDays,
                            used: settlement.data.leaveEquivalent.usedHours.toLocaleString('pl-PL'),
                          })}
                        </li>
                      ) : null}
                      {settlement.data.leaveEquivalent.baseAssumed ? (
                        <li>{t('profile.settlement.baseAssumedNote')}</li>
                      ) : null}
                      {settlement.data.leaveEquivalent.fteAssumed ? (
                        <li>{t('profile.settlement.fteAssumedNote')}</li>
                      ) : null}
                    </>
                  )}
                  {/*
                    Brak odprawy ma powód — bez niego kadry mogłyby uznać, że
                    odprawa się nie należy (przegląd Fali 1).
                  */}
                  {settlement.data.severance === null && settlement.data.severanceUnavailable ? (
                    <li>
                      {t(`profile.settlement.noSeverance.${settlement.data.severanceUnavailable}`)}
                    </li>
                  ) : null}
                  {settlement.data.severance === null ? null : (
                    <>
                      <li>
                        {t('profile.settlement.severance', {
                          months: settlement.data.severance.months,
                          tenure: settlement.data.severance.tenureMonths,
                          amount: formatPLN(settlement.data.severance.amountGr),
                        })}
                      </li>
                      {settlement.data.severance.capped &&
                      settlement.data.severance.capGr !== null ? (
                        <li>
                          {t('profile.settlement.capped', {
                            cap: formatPLN(settlement.data.severance.capGr),
                          })}
                        </li>
                      ) : null}
                      {/*
                        Bez minimalnego wynagrodzenia roku ustania pułapu
                        z art. 8 ust. 4 nie ma z czego policzyć — mówimy to
                        wprost, zamiast pokazać kwotę bez granicy (ADR-0054).
                      */}
                      {settlement.data.severance.capGr === null ? (
                        <li>{t('profile.settlement.capUnknown')}</li>
                      ) : null}
                      <li>
                        {t('profile.settlement.tenureNote', {
                          date: formatDate(settlement.data.severance.tenureFrom, 'dot'),
                        })}
                      </li>
                      <li>{t('profile.settlement.severanceNote')}</li>
                    </>
                  )}
                  {settlement.data.applied.length > 0 ? (
                    <li>
                      {t('profile.settlement.alreadyApplied', {
                        items: settlement.data.applied
                          .map(
                            (item) =>
                              `${t(`profile.settlement.kinds.${item.kind}`)} ${formatPLN(item.amountGr)}`,
                          )
                          .join(', '),
                      })}
                    </li>
                  ) : null}
                </ul>
                {canManagePayroll && settlement.data.openPeriods.length > 0 ? (
                  <div className={styles.dataProtectionActions}>
                    <Select
                      aria-label={t('profile.settlement.period')}
                      value={settlementPeriod || settlement.data.openPeriods[0]!.id}
                      onChange={(event) => setSettlementPeriod(event.target.value)}
                    >
                      {settlement.data.openPeriods.map((period) => (
                        <option key={period.id} value={period.id}>
                          {fmt.month(period.month, 'long')}
                        </option>
                      ))}
                    </Select>
                    {/* Pozycja z tego rozliczenia już jest na liście — drugi raz jej nie dopisujemy (#67). */}
                    {/* Etykieta w <label> — cały napis jest celem dotyku (fala 9, grupa 78). */}
                    <Checkbox
                      label={t('profile.settlement.pickEquivalent')}
                      checked={settlementPick.equivalent && !equivalentApplied}
                      disabled={equivalentApplied}
                      onChange={(event) => {
                        /* Potwierdzenie dotyczyło innego wyboru — po zmianie pytamy od nowa. */
                        setDuplicateOf(null)
                        setSettlementPick({ ...settlementPick, equivalent: event.target.checked })
                      }}
                    />
                    <Checkbox
                      label={t('profile.settlement.pickSeverance')}
                      checked={settlementPick.severance && !severanceApplied}
                      disabled={severanceApplied || settlement.data.severance === null}
                      onChange={(event) => {
                        setDuplicateOf(null)
                        setSettlementPick({ ...settlementPick, severance: event.target.checked })
                      }}
                    />
                    <Button
                      size="sm"
                      disabled={
                        applySettlement.isPending ||
                        (!(settlementPick.equivalent && !equivalentApplied) &&
                          !(settlementPick.severance && !severanceApplied))
                      }
                      onClick={() => {
                        setDuplicateOf(null)
                        applySettlement.mutate({
                          periodId: settlementPeriod || settlement.data!.openPeriods[0]!.id,
                          includeEquivalent: settlementPick.equivalent && !equivalentApplied,
                          includeSeverance: settlementPick.severance && !severanceApplied,
                          confirmKinds: [],
                        })
                      }}
                    >
                      {t('profile.settlement.apply')}
                    </Button>
                  </div>
                ) : null}
                {canManagePayroll && duplicateOf !== null && settlement.data.openPeriods.length > 0 ? (
                  /*
                    Ręczny składnik tego rodzaju może być tą samą kwotą (np.
                    ekwiwalent wpisany z ręki) — albo czymś innym. Rozstrzyga
                    człowiek, a potwierdzenie zostaje w dzienniku (#67).
                  */
                  <div className={styles.dataProtectionActions} role="alert">
                    {duplicateOf.map((conflict) => (
                      <p key={conflict.kind} className={styles.requestNote}>
                        {t('profile.settlement.kindExistsConfirm', {
                          kind: t(`profile.settlement.kinds.${conflict.kind}`),
                          month: conflict.month,
                        })}
                      </p>
                    ))}
                    <Button
                      size="sm"
                      disabled={applySettlement.isPending}
                      onClick={() =>
                        applySettlement.mutate({
                          periodId: settlementPeriod || settlement.data!.openPeriods[0]!.id,
                          includeEquivalent: settlementPick.equivalent && !equivalentApplied,
                          includeSeverance: settlementPick.severance && !severanceApplied,
                          confirmKinds: duplicateOf.map((conflict) => conflict.kind),
                        })
                      }
                    >
                      {t('profile.settlement.confirmDuplicate')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setDuplicateOf(null)}>
                      {t('profile.settlement.cancelDuplicate')}
                    </Button>
                  </div>
                ) : null}
                {canManagePayroll && settlement.data.openPeriods.length === 0 ? (
                  <p className={styles.requestNote}>{t('profile.settlement.noOpenPeriod')}</p>
                ) : null}
              </Card>
            ) : null}

            {tab === 'employment' && dataProtection.data ? (
              /*
                Ochrona danych (Etap 20): termin przechowywania akt bierze się
                z przepisu, nie z ustawienia. Przycisk anonimizacji istnieje
                dopiero wtedy, gdy serwer by go przyjął [D28].
              */
              <Card pad="md" className={styles.annexCard}>
                <p className={styles.annexTitle}>{t('profile.dataProtection.title')}</p>
                <ul className={styles.annexList}>
                  {dataProtection.data.anonymizedAt !== null ? (
                    <li>
                      {t('profile.dataProtection.anonymizedOn', {
                        date: fmt.date(dataProtection.data.anonymizedAt, 'long'),
                      })}
                    </li>
                  ) : dataProtection.data.retention ? (
                    <>
                      <li>
                        {t('profile.dataProtection.until', {
                          date: fmt.date(dataProtection.data.retention.endsOn, 'long'),
                          years: dataProtection.data.retention.years,
                        })}
                      </li>
                      <li>
                        {t(`profile.dataProtection.basis.${dataProtection.data.retention.basis}`)}
                      </li>
                      {dataProtection.data.retention.canBeShortenedByRia ? (
                        <li>{t('profile.dataProtection.riaNote')}</li>
                      ) : null}
                      {dataProtection.data.retention.assumedLongest ? (
                        <li>{t('profile.dataProtection.assumedNote')}</li>
                      ) : null}
                    </>
                  ) : (
                    <li>{t('profile.dataProtection.employed')}</li>
                  )}
                </ul>
                <div className={styles.dataProtectionActions}>
                  {canRevealSensitive ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        openFile(api.employeeDataExportUrl(id))
                      }}
                    >
                      {t('profile.dataProtection.export')}
                    </Button>
                  ) : null}
                </div>
                {dataProtection.data.canAnonymize && canRevealSensitive ? (
                  <div className={styles.dataProtectionActions}>
                    <Input
                      value={anonymizeReason}
                      placeholder={t('profile.dataProtection.reason')}
                      aria-label={t('profile.dataProtection.reason')}
                      onChange={(event) => setAnonymizeReason(event.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={anonymizeReason.trim().length < 5 || anonymize.isPending}
                      onClick={() => anonymize.mutate()}
                    >
                      {t('profile.dataProtection.anonymize')}
                    </Button>
                  </div>
                ) : null}
              </Card>
            ) : null}

            {tab === 'employment' && data.termination === null && canEditPersonnel && employee.status === 'aktywny' ? (
              <Button
                size="sm"
                variant="secondary"
                className={styles.annexButton}
                onClick={() => setOffboardingOpen(true)}
              >
                {t('profile.offboarding.action')}
              </Button>
            ) : null}

            {tab === 'salary' ? (
              /* Brak zakresu to „brak dostępu", brak warunków to stan pusty (grupa 108). */
              !data.scope.compensation ? (
                <NoAccess />
              ) : employmentTerms ? (
                <>
                  <span className={styles.readOnlyLabel}>
                    <ShieldCheckIcon size={13} />
                    {t('profile.readOnly')}
                  </span>
                  <table className={styles.salaryTable}>
                    <tbody>
                      <tr>
                        <td>{t('profile.fields.baseSalary')}</td>
                        <td>
                          {employmentTerms.baseSalaryGr !== null
                            ? formatPLN(employmentTerms.baseSalaryGr, { grosze: 'always' })
                            : '—'}
                        </td>
                      </tr>
                      <tr>
                        <td>{t('profile.fields.bonus')}</td>
                        <td>
                          {employmentTerms.bonusGr !== null
                            ? formatPLN(employmentTerms.bonusGr, { grosze: 'always' })
                            : '—'}
                        </td>
                      </tr>
                      <tr className={styles.salaryTotal}>
                        <td>{t('profile.fields.gross')}</td>
                        <td>
                          {formatPLN(
                            (employmentTerms.baseSalaryGr ?? 0) + (employmentTerms.bonusGr ?? 0),
                            { grosze: 'always' },
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <ApprovalFooter
                    approvedBy={employmentTerms.preparedBy}
                    approvedAt={employmentTerms.acceptedAt}
                    labelKey="profile.termsAcceptedFooter"
                  />

                  {/*
                    Aneks (Etap 14): kadry proponują nowe warunki z datą
                    obowiązywania, pracownik przyjmuje je w portalu. Silnik płac
                    bierze warunki OBOWIĄZUJĄCE w okresie, więc aneks nie rusza
                    list z wcześniejszych miesięcy.
                  */}
                  {data.termsProposal ? (
                    <Card pad="md" className={styles.annexCard}>
                      <p className={styles.annexTitle}>
                        {t('profile.annex.pending', {
                          date: data.termsProposal.startDate
                            ? fmt.date(data.termsProposal.startDate, 'long')
                            : '—',
                        })}
                      </p>
                      <ul className={styles.annexList}>
                        {data.termsProposal.changes.map((change) => (
                          <li key={change.field}>
                            {t(`portal.terms.fields.${change.field}`)}:{' '}
                            {annexValue(change.field, change.before)} →{' '}
                            {annexValue(change.field, change.after)}
                          </li>
                        ))}
                      </ul>
                      {canEditPersonnel ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={withdrawAnnex.isPending}
                          onClick={() => withdrawAnnex.mutate()}
                        >
                          {t('profile.annex.withdraw')}
                        </Button>
                      ) : null}
                    </Card>
                  ) : canEditPersonnel && employee.status === 'aktywny' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      className={styles.annexButton}
                      onClick={() => setAnnexOpen(true)}
                    >
                      {t('profile.annex.prepare')}
                    </Button>
                  ) : null}
                  {/*
                    Tytuły do potrącenia z wynagrodzenia (Etap 33): granice
                    z art. 87 k.p. i kwotę wolną liczy silnik przy naliczeniu.
                  */}
                  <Garnishments employeeId={employee.id} canEdit={canManagePayroll} />
                </>
              ) : data.termsProposal ? (
                /*
                  Warunki przygotowane, ale jeszcze nieprzyjęte (etap „warunki
                  przygotowane" procesu zatrudnienia): stan pusty „nie są
                  przygotowane" mówiłby nieprawdę (Fala 4, po przeglądzie).
                */
                <Card pad="md" className={styles.annexCard}>
                  <p className={styles.annexTitle}>{t('profile.salaryAwaitingAcceptance')}</p>
                  <p className={styles.annexNote}>{t('profile.salaryAwaitingAcceptanceBody')}</p>
                  <ul className={styles.annexList}>
                    {data.termsProposal.changes.map((change) => (
                      <li key={change.field}>
                        {t(`portal.terms.fields.${change.field}`)}:{' '}
                        {change.field === 'contractType' && change.after !== null
                          ? t(`contractType.${change.after}`, { defaultValue: change.after })
                          : annexValue(change.field, change.after)}
                      </li>
                    ))}
                  </ul>
                </Card>
              ) : (
                <EmptyState
                  title={t('profile.salaryNotPrepared')}
                  description={t('profile.salaryNotPreparedBody')}
                />
              )
            ) : null}

            {tab === 'bank' ? (
              !data.scope.compensation ? (
                <NoAccess />
              ) : bankAccount ? (
                <>
                  <span className={styles.readOnlyLabel}>
                    <ShieldCheckIcon size={13} />
                    {t('profile.readOnly')}
                  </span>
                  <div className={styles.fieldGrid}>
                    <Field
                      label={t('profile.fields.iban')}
                      value={revealed.iban ?? bankAccount.iban}
                      mono
                      {...(bankAccount.iban ? { action: revealAction('iban') } : {})}
                    />
                    <Field label={t('profile.fields.bank')} value={bankAccount.bankName} />
                    <Field
                      label={t('profile.fields.accountOwner')}
                      value={bankAccount.ownerName}
                    />
                    <Field label={t('profile.fields.currency')} value={bankAccount.currency} />
                  </div>
                  <ApprovalFooter
                    approvedBy={bankAccount.approvedBy}
                    approvedAt={bankAccount.approvedAt}
                    {...(canRequestChange
                      ? { onRequestChange: () => setChangeField('bank_account') }
                      : {})}
                  />
                </>
              ) : (
                <EmptyState title={t('profile.bankMissing')} description={t('profile.bankMissingBody')} />
              )
            ) : null}

            {/*
              Zakładka „Dokumenty" jest wykazem akt osobowych z § 4
              rozporządzenia o dokumentacji pracowniczej: części A–E, numeracja
              z chronologii, podczęści na sprawy w D (Etap 40).
            */}
            {tab === 'documents' ? (
              canViewDocuments ? (
                <PersonnelFile employeeId={employee.id} canEdit={canEditDocuments} />
              ) : (
                <NoAccess />
              )
            ) : null}

            {tab === 'health' ? (
              /* Badania i BHP to dane o zdrowiu — bez „Badania : Podgląd" zakładka mówi o braku dostępu (grupa 63). */
              data.scope.health === false ||
              data.medicalExaminations === null ||
              data.bhpTrainings === null ? (
                <NoAccess />
              ) : data.medicalExaminations.length === 0 && data.bhpTrainings.length === 0 ? (
                <EmptyState title={t('profile.emptyHealth')} description={t('empty.generic')} />
              ) : (
                /*
                 * Wiersze z kluczy i18n i stanu z reguły wspólnej z listą kadr
                 * (fala 8, grupa 109) — bez surowych „approved" i „stanowiskowe".
                 */
                <ul className={styles.list}>
                  {data.medicalExaminations.map((exam, index) => (
                    <li key={`med-${index}`} className={styles.listItem}>
                      <span>
                        {t('profile.health.medical')}
                        {exam.validUntil
                          ? ` · ${t('profile.health.validUntil', { date: formatDate(exam.validUntil, 'dot') })}`
                          : exam.referralDueOn
                            ? ` · ${t('profile.health.referralDue', { date: formatDate(exam.referralDueOn, 'dot') })}`
                            : ''}
                      </span>
                      <StatusPill tone={HEALTH_TONE[exam.state]}>{t(`profile.health.state.${exam.state}`)}</StatusPill>
                    </li>
                  ))}
                  {data.bhpTrainings.map((training, index) => (
                    <li key={`bhp-${index}`} className={styles.listItem}>
                      <span>
                        {t(`portal.health.kinds.${training.kind}`, { defaultValue: t('portal.health.bhp') })}
                        {training.performedAt
                          ? ` · ${t('profile.health.performed', { date: formatDate(training.performedAt, 'dot') })}`
                          : training.referralDueOn
                            ? ` · ${t('profile.health.referralDue', { date: formatDate(training.referralDueOn, 'dot') })}`
                            : ''}
                        {training.conductedBy
                          ? ` · ${t('profile.health.conductedBy', { who: training.conductedBy })}`
                          : ''}
                      </span>
                      <StatusPill tone={HEALTH_TONE[training.state]}>
                        {training.state === 'ok' &&
                        training.validUntil === null &&
                        ONE_TIME_BHP_KINDS.includes(training.kind as BhpTrainingKind)
                          ? t('profile.health.passed')
                          : t(`profile.health.state.${training.state}`)}
                      </StatusPill>
                    </li>
                  ))}
                </ul>
              )
            ) : null}

            {tab === 'requests' ? (
              /* Wnioski o zmianę danych widzą kadry — przełożony dostałby odmowę albo własne wnioski. */
              canViewPersonnel ? (
                <ChangeRequestsTab employeeId={employee.id} canDecide={canRunProcess} />
              ) : (
                <NoAccess />
              )
            ) : null}

            {tab === 'audit' ? (
              <>
                <p className={styles.auditNote}>
                  <ShieldCheckIcon size={15} />
                  {t('profile.auditNote')}
                </p>
                {audit.isPending ? (
                  <Spinner label={t('common.loading')} />
                ) : (audit.data?.items.length ?? 0) === 0 ? (
                  <EmptyState title={t('profile.emptyAudit')} />
                ) : (
                  <div>
                    {audit.data?.items.map((entry) => (
                      <div key={entry.id} className={styles.auditRow}>
                        <span className={styles.auditDot} aria-hidden="true" />
                        {/*
                         * Typ operacji, pole i — w zakresie roli — porównanie
                         * przed/po (§17 pkt 2). Surowej etykiety API nie oddaje (#56).
                         */}
                        <span>
                          {t(`auditActions.${entry.action}`, {
                            defaultValue: t('profile.auditEvent'),
                          })}
                          {entry.field ? ` · ${t(`requestFields.${entry.field}`)}` : null}
                          {entry.change
                            ? `: ${entry.change.before ?? t('changeRequests.empty_value')} → ${
                                entry.change.after ?? t('changeRequests.empty_value')
                              }`
                            : null}
                          {entry.detail ? ` · ${entry.detail}` : null}
                        </span>
                        <span className={styles.auditMeta}>
                          {entry.actorLabel} · {formatDateTime(entry.createdAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </Card>

        <Card pad="lg">
          <h3 className={styles.progressTitle}>{t('profile.progressTitle')}</h3>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
          </div>
          <p className={styles.progressLevel}>
            {onboarding.stageNumber >= onboarding.totalStages
              ? t('profile.progressDone')
              : t('profile.progressLevel', {
                  current: onboarding.stageNumber,
                  total: onboarding.totalStages,
                })}
          </p>
          <p className={styles.progressNext}>
            <Info size={15} />
            {onboarding.employmentEnded
              ? t('profile.employmentEndedNext', {
                  date: onboarding.employmentEndedOn ? formatDate(onboarding.employmentEndedOn, 'dot') : '—',
                })
              : t(nextStepKey)}
          </p>

          {data.checklist.length > 0 ? (
            <div className={styles.checklist}>
              <p className={styles.checklistHead}>
                <span className={styles.checklistTitle}>{t('profile.checklistTitle')}</span>
                <span className={styles.checklistPercent}>
                  {t('profile.checklistPercent', {
                    percent: fileChecklistPercent(doneItems, data.checklist.length),
                    done: doneItems,
                    total: data.checklist.length,
                  })}
                </span>
              </p>

              {data.checklist.map((item) => {
                const manual = isManualFileChecklistKey(item.itemKey)
                return (
                  <p key={item.itemKey} className={styles.checklistRow}>
                    <span className={item.done ? styles.checklistDone : styles.checklistTodo}>
                      {item.done ? <Check size={15} /> : '○'}
                    </span>
                    <span className={styles.checklistLabel}>
                      {/* Nazwę według bieżącej umowy liczy serwer — z tych samych warunków co pozycje (Fala 6). */}
                      {t(`profile.checklistItems.${item.labelKey ?? item.itemKey}`, {
                        defaultValue: item.itemKey,
                      })}
                      {item.note ? ` — ${item.note}` : ''}
                    </span>
                    {/*
                      Pozycje wynikające z dowodu w aktach są tylko do wglądu —
                      ręczna zmiana rozjechałaby je z rzeczywistością przy
                      najbliższym przeliczeniu. Papierowe odhacza dział HR.
                    */}
                    {manual && canRunProcess ? (
                      <button
                        type="button"
                        className={styles.checklistToggle}
                        disabled={markItem.isPending}
                        onClick={() =>
                          markItem.mutate({ itemKey: item.itemKey, done: !item.done })
                        }
                      >
                        {t(item.done ? 'profile.checklistUnmark' : 'profile.checklistMark')}
                      </button>
                    ) : null}
                  </p>
                )
              })}
            </div>
          ) : null}
        </Card>

        {/*
          Panel procesu prowadzi dział HR — pozostałe role widzą profil,
          ale nie przesuwają cudzego zatrudnienia.
        */}
        {canRunProcess ? (
          <OnboardingPanel
            employeeId={employee.id}
            employmentEndedOn={onboarding.employmentEnded ? (onboarding.employmentEndedOn ?? '') : null}
          />
        ) : null}
      </div>

      {offboardingOpen ? (
        <OffboardingModal employeeId={id} onClose={() => setOffboardingOpen(false)} />
      ) : null}

      {annexOpen ? (
        <TermsChangeModal
          employeeId={id}
          /* Baza aneksu to ostatnia przyjęta wersja, nie dzisiejsza (przegląd Fali 6). */
          current={data.annexBase ?? employmentTerms}
          onClose={() => setAnnexOpen(false)}
        />
      ) : null}

      {changeField !== null ? (
        <ChangeRequestModal
          open
          onClose={() => setChangeField(null)}
          employeeId={employee.id}
          field={changeField}
          currentValue={currentValueOf(changeField, data)}
          onSubmitted={() => setTab('requests')}
        />
      ) : null}
    </>
  )
}

/** Wartość prezentacyjna pola akt — modal pokazuje ją obok formularza. */
function currentValueOf(
  field: ChangeRequestField,
  data: EmployeeProfileResponse,
): string | null {
  if (field === 'bank_account') return data.bankAccount?.iban ?? null
  if (field === 'tax_office') return data.taxData?.taxOffice ?? null
  if (field === 'nfz_branch') return data.taxData?.nfzBranch ?? null
  if (field === 'phone') return data.personalData?.phone ?? null
  if (field === 'private_email') return data.personalData?.privateEmail ?? null
  return (
    [data.personalData?.addressStreet, data.personalData?.addressCityZip]
      .filter(Boolean)
      .join(', ') || null
  )
}
